import {
    type AnyConnectionType,
    type EdgeForConnection,
    type NodeForConnection,
    type PageInfoForConnection,
    getConnectionEdges,
    getConnectionNodes,
    getConnectionPageInfo,
    getTotalCount,
    connectionHasNodes,
} from '@precon-tools-suite-by-shared/precon-graphql-types';

import { eagerComputed } from '@vueuse/core';
import {
    type MaybeRefOrGetter,
    toRef,
    type Ref,
    type ComputedRef,
    computed,
} from 'vue';

export type UseConnectionResult<C extends AnyConnectionType> = {
    /**
     * The connection object itself.
     */
    connection: ComputedRef<C | null | undefined>;
    /**
     * The edges in the connection. Will return an empty when connection or edges are not loaded.
     */
    edges: ComputedRef<EdgeForConnection<C>[]>;
    /**
     * The nodes in the connection. These will use the `nodes` field if available. Otherwise, it will give the `edges.node` field.
     */
    nodes: ComputedRef<NodeForConnection<C>[]>;
    /**
     * Gives the page info as defined on the connection. It will give an empty PageInfo object.
     */
    pageInfo: ComputedRef<PageInfoForConnection<C>>;
    /**
     * Gives the total count of the page info.
     */
    totalCount: ComputedRef<number | null>;
    /**
     * Whether there is a next page to be loaded. Always `false` when no page info was defined.
     */
    hasNextPage: Readonly<Ref<boolean | undefined>>;
    /**
     * Whether there is a previous page to be loaded. Always `false` when no page info was defined.
     */
    hasPreviousPage: Readonly<Ref<boolean | undefined>>;
    /**
     * Whether there are more pages that can be found using the current pagination method.
     *
     * Value is `true` iff `hasNextPage.value` or `hasPreviousPage.value`.
     */
    hasMore: Readonly<Ref<boolean | undefined>>;
    /**
     * The cursor of the first edge on the page. Will always be `undefined` if the PageInfo was not defined.
     */
    startCursor: ComputedRef<string | null | undefined>;
    /**
     * The cursor of the last edge on the page. Will always be `undefined` if the PageInfo was not defined.
     */
    endCursor: ComputedRef<string | null | undefined>;
    /**
     * The total amount of edges on the current page in this connection.
     */
    edgeCount: ComputedRef<number>;
    /**
     * The total amount of nodes on the current page in this connection.
     */
    nodeCount: ComputedRef<number>;
    /**
     * The maximum of the edgeCount and the nodeCount.
     */
    count: ComputedRef<number>;
    /**
     * Guess whether this connection has no edges. Will be `undefined` if it cannot be determined.
     */
    isEmpty: Readonly<Ref<boolean | undefined>>;
    /**
     * Guess whether this connection has any edges. Will be `undefined` if it cannot be determined.
     */
    isNonEmpty: Readonly<Ref<boolean | undefined>>;
};

/**
 * Destructures a connection-type object into its
 *
 * @param connection The input connection object.
 */
export function useConnection<C extends AnyConnectionType>(
    connection: MaybeRefOrGetter<C | null | undefined>
): UseConnectionResult<C> {
    const conn = toRef(connection) as ComputedRef<C | null | undefined>;
    const edges = computed(() => getConnectionEdges(conn.value));
    const nodes = computed(() => getConnectionNodes(conn.value));
    const totalCount = computed(() => getTotalCount(conn.value));
    const pageInfo = computed(() => getConnectionPageInfo(conn.value));

    const hasNextPage = eagerComputed(() => pageInfo.value.hasNextPage);
    const hasPreviousPage = eagerComputed(() => pageInfo.value.hasPreviousPage);
    const hasMore = eagerComputed(
        () => hasNextPage.value || hasPreviousPage.value
    );
    const startCursor = computed(() => pageInfo.value.startCursor);
    const endCursor = computed(() => pageInfo.value.endCursor);

    const edgeCount = computed(() => edges.value.length);
    const nodeCount = computed(() =>
        connectionHasNodes(conn.value) ? nodes.value.length : edgeCount.value
    );
    const count = computed(() => Math.max(edgeCount.value, nodeCount.value));

    const isEmpty = eagerComputed(() => {
        if (totalCount.value !== null) return totalCount.value === 0;
        if (count.value > 0) return false;
        if (
            hasNextPage.value === undefined ||
            hasPreviousPage.value === undefined
        )
            return undefined;
        return !hasNextPage.value && !hasPreviousPage.value;
    });
    const isNonEmpty = eagerComputed(() =>
        isEmpty.value === undefined ? undefined : !isEmpty.value
    );

    return {
        connection: conn,
        edges,
        nodes,
        pageInfo,
        totalCount,
        hasNextPage,
        hasPreviousPage,
        hasMore,
        startCursor,
        endCursor,
        edgeCount,
        nodeCount,
        count,
        isEmpty,
        isNonEmpty,
    };
}
